// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Image, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import LocalService from '../../services/LocalService';
import TypeLocalService from '../../services/TypeLocalService';

const LocalsListItems = () => {
	const [originalLocals, setOriginalLocals] = useState([]);
	const [locals, setLocals] = useState([]);
	const [type, setTypeLocal] = useState([]); // Add this line
	const [typeOptions, setTypeOptions] = useState([]);

	useEffect(() => {
		const fetchClassOptions = async () => {
			try {
				const types = await TypeLocalService.getTypeLocalOptions();
				setTypeOptions(types);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchClassOptions();
	}, []);

	const TYPE_CHOICES = typeOptions;

	useEffect(() => {
		Promise.all([
			LocalService.getAllLocals(),
			TypeLocalService.getAllTypeLocals()
		]).then(([elevesData, typeData]) => {
			setOriginalLocals(elevesData);
			setLocals(elevesData);
			setTypeLocal(typeData);
		});
	}, []);

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else if (columnId === 'status' && value) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else if (
				columnId === 'description' ||
				(columnId === 'equipements' && value)
			) {
				return value.replace(/[^-'\s]+/g, function (word) {
					return word.replace(/^./, function (first) {
						return first.toUpperCase();
					});
				});
			} else {
				return value;
			}
		} else if (columnId === 'type') {
			return value && value.type ? value.type.toUpperCase() : ''; // Return the type property
		}
		return value;
	};

	const getInitials = (local) => {
		const lastName = local.nom ? local.nom.charAt(0).toUpperCase() : '';
		return `${lastName}`;
	};

	const getTypeLocalById = (typeId) => {
		return type.find((item) => item.id === typeId) ?? {};
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					return (
						<div className="d-flex align-items-center">
							{value ? (
								<Link to={`/locaux/autres/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'equipements',
				Header: 'Equipement',
				Cell: ({ value }) => <div>{formatValue(value, 'equipements')}</div>
			},

			{
				accessor: 'status',
				Header: 'Status',
				Cell: ({ value }) => <div>{formatValue(value, 'status')}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return locals.map((local) => ({
			...local,
			type: getTypeLocalById(local.type)
		}));
	}, [locals]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newLocalsList = originalLocals.filter((local) => {
				return local.type === Number(filterTerm);
			});
			setLocals(newLocalsList);
		} else {
			setLocals(originalLocals);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xl={2} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un local"
						/>
					</Col>
					<Col xxl={2} lg={2} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par type"
							options={TYPE_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default LocalsListItems;
